
import { Options, Vue, prop } from "vue-class-component";
import Tooltip from "@/app/ui/components/tooltip/index.vue";
import { BookingController } from "@/app/ui/controllers/BookingController";
import {
  UploadHydraFileRequest,
  DeleteHydraFileRequest
} from "@/data/payload/api/BookingRequest";
import { UploadMultiple } from "@/domain/entities/MainApp";
import needsAuth from "@/app/infrastructures/misc/common-library/NeedsAuth";
import parsingLinkGcpToAws from "@/app/infrastructures/misc/common-library/ParsingLinkGcpToAws";
import { getToken } from "@/app/infrastructures/misc/Cookies";

class Props {
  photos = prop<{ url: string; type: string }[]>({
    default: [],
    type: Array
  });
}

const seedImages = new Array(15).fill("");

@Options({
  emits: ["updateFiles", "setLoadingUpload"],
  components: {
    Tooltip
  }
})
export default class FileAttachment extends Vue.with(Props) {
  private controller: AbortController | null = null;
  get proofPhotos() {
    const labels = [];
    let i = 1;

    while (i <= 15) {
      labels.push(`${this.$t("partnerManagement.label.attachment")} ${i}`);
      i++;
    }

    return labels;
  }

  attachmentImage: any = [];
  storageImages: any = [];
  async mounted() {
    this.storageImages = await Promise.all(
      seedImages.map((_, index) => this.resolve(this.photos[index]?.url || ""))
    );
  }
  isLoading = false;
  isLoadingDelete = false;
  isError = false;
  errorMessage = "";

  async upload(params: UploadMultiple) {
    this.isLoading = true;
    this.setLoadingUpload(true);
    this.isError = false;
    this.errorMessage = "";
    try {
      if (params.file) {
        const fileLink = await BookingController.uploadHydraFileV2(
          new UploadHydraFileRequest({
            attachFile: params.file,
            folderPath: "pos/",
            tag: "pos_supporting_files"
          })
        );
        this.storageImages[params.index] = fileLink.data;
        this.updateFiles();
      }
    } catch (err) {
      this.isError = true;
      this.errorMessage = this.$t("partnerManagement.error.uploadFile", {
        value: params.index + 1
      });
    }
    this.isLoading = false;
    this.setLoadingUpload(false);
  }

  async remove(index: number) {
    this.isLoadingDelete = true;
    this.setLoadingUpload(true);
    this.isError = false;
    this.errorMessage = "";
    try {
      const del = await BookingController.deleteHydraFileV2(
        new DeleteHydraFileRequest({
          attachFiles: [this.storageImages[index]]
        })
      );
      const statusDel = del.data[this.storageImages[index]];
      statusDel ? this.deleteFiles(index) : this.erroMessageDelete(index);
    } catch (err) {
      this.erroMessageDelete(index);
    }
    this.isLoadingDelete = false;
    this.setLoadingUpload(false);
  }

  get mergedFiles() {
    return this.storageImages.map((item: string, index: number) => {
      return item.includes("blob") ? this.photos[index].url : item;
    });
  }

  updateFiles() {
    this.$emit("updateFiles", this.mergedFiles);
  }

  setLoadingUpload(status: boolean) {
    this.$emit("setLoadingUpload", status);
  }

  deleteFiles(index: number) {
    this.storageImages[index] = "";
    this.updateFiles();
  }

  erroMessageDelete(index: number) {
    this.deleteFiles(index);
  }

  get validateLimitUpload() {
    const images = this.storageImages.filter((item: string) => item);
    return images.length > 0 && images.length < 3;
  }

  getType(url: string): string {
    const parts = url.split("?")[0].split(".");
    const ext = parts[parts.length - 1] || "";
    if (needsAuth(url)) return url.slice(-3);
    return ext.toLowerCase();
  }

  private async resolve(url?: string) {
    if (!url) {
      return Promise.resolve("");
    }
    if (!needsAuth(url) || this.getType(url) === "pdf") {
      return Promise.resolve(url);
    }

    try {
      const parsed = parsingLinkGcpToAws(url);
      this.controller = new AbortController();
      const headers = new Headers();
      headers.append("authorization", `Bearer ${getToken()}`);
      const res = await fetch(parsed, {
        headers
      });
      if (!res.ok) {
        // fallback to direct src if auth fetch fails
        return Promise.resolve(url);
      }
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      return Promise.resolve(objectUrl);
    } catch (e) {
      return Promise.resolve(url);
    }
  }

  getTypeFile(index: number) {
    if (index >= this.photos.length) return "";

    return this.photos[index].type;
  }
}
